import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Carousel } from "react-bootstrap";

import { ThemeContext } from "../../../context/ThemeContext";
import { GlobalSet } from "../global/GlobalProvider";

/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const CURRENCY_SYMBOL = "৳";

/*
 * Refresh dashboard every 5 minutes.
 * Change this value if you need a different refresh time.
 */
const REFRESH_INTERVAL = 5 * 60 * 1000;

/* =========================================================
   HELPERS
========================================================= */

const formatMoney = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
};

const buildImageUrl = (baseUrl, path) => {
  if (!path) return "";

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:")
  ) {
    return path;
  }

  const base = String(baseUrl || "").replace(/\/+$/, "");
  const imagePath = String(path).replace(/^\/+/, "");

  return `${base}/${imagePath}`;
};

/* =========================================================
   ICONS
========================================================= */

const WalletIcon = ({ type }) => {
  const common = {
    width: 25,
    height: 25,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  if (type === "main") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="M16 10h5v4h-5a2 2 0 0 1 0-4Z" />
        <path d="M7 9h5" />
      </svg>
    );
  }

  if (type === "deposit") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="M3 10h18" />
        <path d="M8 15h3" />
      </svg>
    );
  }

  if (type === "principal") {
    return (
      <svg {...common}>
        <path d="M4 9h16" />
        <path d="M6 9V6l6-3 6 3v3" />
        <path d="M6 9v9" />
        <path d="M10 9v9" />
        <path d="M14 9v9" />
        <path d="M18 9v9" />
        <path d="M4 18h16" />
      </svg>
    );
  }

  if (type === "live") {
    return (
      <svg {...common}>
        <path d="M4 17 9 12l3 3 7-8" />
        <path d="M14 7h5v5" />
      </svg>
    );
  }

  if (type === "promo") {
    return (
      <svg {...common}>
        <path d="M20 12v8H4v-8" />
        <path d="M2 7h20v5H2z" />
        <path d="M12 7v13" />
        <path d="M12 7H7.5A2.5 2.5 0 1 1 10 4.5L12 7Z" />
        <path d="M12 7h4.5A2.5 2.5 0 1 0 14 4.5L12 7Z" />
      </svg>
    );
  }

  if (type === "totalDeposit") {
    return (
      <svg {...common}>
        <path d="M12 3v12" />
        <path d="m7 10 5 5 5-5" />
        <path d="M5 21h14" />
      </svg>
    );
  }

  if (type === "income") {
    return (
      <svg {...common}>
        <path d="m4 17 5-5 4 4 7-8" />
        <path d="M15 8h5v5" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12 21V9" />
      <path d="m7 14 5-5 5 5" />
      <path d="M5 3h14" />
    </svg>
  );
};

/* =========================================================
   WALLET CARD
========================================================= */

const WalletCard = ({ item }) => {
  return (
    <article className={`hm-wallet-card hm-wallet-${item.type}`}>
      <div className="hm-wallet-top">
        <div className="hm-wallet-icon">
          <WalletIcon type={item.type} />
        </div>

        <span className="hm-wallet-tag">
          {item.tag}
        </span>
      </div>

      <div className="hm-wallet-label">
        {item.title}
      </div>

      <div className="hm-wallet-value">
        <span>{CURRENCY_SYMBOL}</span>
        {formatMoney(item.value)}
      </div>

      <p>{item.description}</p>
    </article>
  );
};

/* =========================================================
   HOME
========================================================= */

const Home = () => {
  const { changeBackground } = useContext(ThemeContext);
  const { BaseUrl } = GlobalSet();

  const [homeData, setHomeData] = useState({});
  const [slider, setSlider] = useState([]);
  const [tradingWallet, setTradingWallet] = useState(0);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  /* =========================================================
     PAGE / THEME
  ========================================================= */

  useEffect(() => {
    document.title = "Rajya Properties Home";

    changeBackground({
      value: "light",
      label: "Light",
    });
  }, [changeBackground]);

  /* =========================================================
     API
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const loadHomeData = async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setError("");

        const response = await axios.post("/home-data", {
          key_s: API_KEY,
        });

        if (!mounted) return;

        setSlider(
          Array.isArray(response?.data?.slider)
            ? response.data.slider
            : []
        );

        setHomeData(
          response?.data?.data_info &&
            typeof response.data.data_info === "object"
            ? response.data.data_info
            : {}
        );

        setTradingWallet(
          response?.data?.trading_wallet ?? 0
        );
      } catch (err) {
        console.error("Home data error:", err);

        if (!mounted) return;

        setError(
          err?.response?.data?.message ||
            "Unable to load dashboard information. Please try again."
        );
      } finally {
        if (mounted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    loadHomeData(false);

    const interval = setInterval(() => {
      loadHomeData(true);
    }, REFRESH_INTERVAL);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  /* =========================================================
     WALLET DATA
  ========================================================= */

  const wallets = useMemo(
    () => [
      {
        type: "main",
        title: "Main Wallet",
        value: homeData.balance,
        tag: "Available",
        description: "Your available account balance",
      },
      {
        type: "deposit",
        title: "Deposit Wallet",
        value: homeData.reserve_balance,
        tag: "Deposit",
        description: "Funds available in deposit wallet",
      },
      {
        type: "principal",
        title: "Principal Wallet",
        value: homeData.investing_wallet,
        tag: "Capital",
        description: "Your principal investment balance",
      },
      {
        type: "live",
        title: "Live Investing",
        value: tradingWallet,
        tag: "Live",
        description: "Current live investing balance",
      },
      {
        type: "promo",
        title: "Promo Wallet",
        value: 0,
        tag: "Promo",
        description: "Promotional wallet balance",
      },
      {
        type: "totalDeposit",
        title: "Total Deposit",
        value: homeData.total_deposit,
        tag: "Lifetime",
        description: "Total amount deposited",
      },
      {
        type: "income",
        title: "Total Income",
        value: homeData.total_Income,
        tag: "Earning",
        description: "Total income earned",
      },
      {
        type: "withdraw",
        title: "Total Withdrawal",
        value: homeData.total_withdraw,
        tag: "Lifetime",
        description: "Total amount withdrawn",
      },
    ],
    [homeData, tradingWallet]
  );

  /* =========================================================
     TOTAL SUMMARY
  ========================================================= */

  const summary = useMemo(() => {
    const available =
      Number(homeData.balance || 0) +
      Number(homeData.reserve_balance || 0) +
      Number(homeData.investing_wallet || 0) +
      Number(tradingWallet || 0);

    return {
      available,
      income: Number(homeData.total_Income || 0),
      deposit: Number(homeData.total_deposit || 0),
    };
  }, [homeData, tradingWallet]);

  /* =========================================================
     LOADING
  ========================================================= */

  /* Initial loading is rendered only inside data sections. */

  /* =========================================================
     MAIN
  ========================================================= */

  return (
    <Fragment>
      <HomeStyles />

      <main className="hm-page">
        <div className="hm-container">


          {/* ===============================================
              ERROR NOTICE
          =============================================== */}

          {error && (
            <div className="hm-error-notice">
              <div className="hm-error-symbol">!</div>

              <div>
                <strong>Dashboard Update Failed</strong>
                <span>{error}</span>
              </div>
            </div>
          )}


          {/* ===============================================
              SUMMARY
          =============================================== */}

          <section className="hm-summary-grid">
            {loading ? (
              <Fragment>
                <div className="hm-summary-main hm-data-skeleton">
                  <span className="hm-skeleton-line hm-sk-label"></span>
                  <span className="hm-skeleton-line hm-sk-value"></span>
                  <span className="hm-skeleton-line hm-sk-text"></span>
                </div>
                {[1, 2].map((item) => (
                  <div className="hm-summary-mini hm-data-skeleton" key={item}>
                    <span className="hm-skeleton-line hm-sk-label"></span>
                    <span className="hm-skeleton-line hm-sk-mini-value"></span>
                  </div>
                ))}
              </Fragment>
            ) : (
              <Fragment>
                <div className="hm-summary-main">
                  <span>Total Active Balance</span>
                  <strong>
                    <small>{CURRENCY_SYMBOL}</small>
                    {formatMoney(summary.available)}
                  </strong>
                  <p>
                    Combined Main, Deposit, Principal and Live
                    Investing wallets
                  </p>
                </div>

                <div className="hm-summary-mini">
                  <span>Total Deposit</span>
                  <strong>
                    {CURRENCY_SYMBOL}
                    {formatMoney(summary.deposit)}
                  </strong>
                </div>

                <div className="hm-summary-mini">
                  <span>Total Income</span>
                  <strong>
                    {CURRENCY_SYMBOL}
                    {formatMoney(summary.income)}
                  </strong>
                </div>
              </Fragment>
            )}
          </section>

          {/* ===============================================
              WALLET SECTION
          =============================================== */}

          <section className="hm-wallet-section">
            <div className="hm-section-heading">
              <div>
                <span>My Account</span>
                <h2>Wallet Overview</h2>
              </div>

              <p>
                All wallet balances and account totals
              </p>
            </div>

            <div className="hm-wallet-grid">
              {loading
                ? Array.from({ length: 8 }).map((_, index) => (
                    <article
                      className="hm-wallet-card hm-data-skeleton"
                      key={`wallet-skeleton-${index}`}
                    >
                      <div className="hm-wallet-top">
                        <span className="hm-skeleton-box hm-sk-icon"></span>
                        <span className="hm-skeleton-line hm-sk-tag"></span>
                      </div>
                      <span className="hm-skeleton-line hm-sk-wallet-label"></span>
                      <span className="hm-skeleton-line hm-sk-wallet-value"></span>
                      <span className="hm-skeleton-line hm-sk-wallet-text"></span>
                    </article>
                  ))
                : wallets.map((item) => (
                    <WalletCard
                      key={item.type}
                      item={item}
                    />
                  ))}
            </div>
          </section>

        </div>
      </main>
    </Fragment>
  );
};

/* =========================================================
   STYLES
========================================================= */

const HomeStyles = () => (
  <style>{`
    .hm-page {
      --hm-primary: #635bff;
      --hm-primary-dark: #4f46d7;
      --hm-text: #182230;
      --hm-muted: #667085;
      --hm-border: #e7e9ef;
      --hm-bg: #f7f8fb;
      --hm-green: #12b76a;
      --hm-orange: #f79009;
      --hm-red: #f04438;

      min-height: 100vh;
      padding: 0 0 35px;
      background:
        radial-gradient(
          circle at 0 0,
          rgba(99, 91, 255, 0.05),
          transparent 25%
        ),
        var(--hm-bg);
      color: var(--hm-text);
      font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
    }

    .hm-container {
      width: 100%;
      margin: 0 auto;
    }

    /* =====================================================
       SLIDER
    ===================================================== */

    .hm-slider-wrap {
      overflow: hidden;
      margin-bottom: 24px;
      border-radius: 0 0 22px 22px;
      background: #fff;
      box-shadow: 0 8px 30px rgba(31, 38, 65, 0.08);
    }

    .hm-slide {
      position: relative;
      width: 100%;
      overflow: hidden;
      background: #eceef3;
    }

    .hm-slide img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }

    .hm-carousel .carousel-control-prev,
    .hm-carousel .carousel-control-next {
      width: 7%;
    }

    .hm-carousel .carousel-indicators {
      margin-bottom: 12px;
    }

    .hm-carousel .carousel-indicators button {
      width: 8px;
      height: 8px;
      border: 0;
      border-radius: 50%;
    }

    /* =====================================================
       HEADER
    ===================================================== */

    .hm-dashboard-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 25px;
      padding: 27px 30px;
      margin: 0 0 18px;
      border-radius: 22px;
      background:
        linear-gradient(
          135deg,
          #171b2d 0%,
          #252b48 58%,
          #393568 100%
        );
      box-shadow: 0 16px 42px rgba(31, 38, 65, 0.11);
    }

    .hm-eyebrow {
      display: block;
      margin-bottom: 5px;
      color: #c9c5ff;
      font-size: 13px;
      font-weight: 800;
      letter-spacing: 0.7px;
      text-transform: uppercase;
    }

    .hm-dashboard-header h1 {
      margin: 0 0 7px;
      color: #fff;
      font-size: 30px;
      font-weight: 850;
      line-height: 1.25;
    }

    .hm-dashboard-header p {
      max-width: 650px;
      margin: 0;
      color: rgba(255, 255, 255, 0.67);
      font-size: 15px;
      line-height: 1.6;
    }

    .hm-live-status {
      flex: 0 0 auto;
      min-width: 180px;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 13px 16px;
      border: 1px solid rgba(255, 255, 255, 0.11);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.07);
    }

    .hm-live-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #75e5aa;
      box-shadow: 0 0 0 5px rgba(117, 229, 170, 0.12);
    }

    .hm-live-dot.hm-refreshing {
      animation: hm-pulse 1s ease-in-out infinite;
    }

    @keyframes hm-pulse {
      50% {
        opacity: 0.35;
      }
    }

    .hm-live-status small {
      display: block;
      margin-bottom: 2px;
      color: rgba(255, 255, 255, 0.52);
      font-size: 12px;
    }

    .hm-live-status strong {
      display: block;
      color: #fff;
      font-size: 15px;
      font-weight: 800;
    }

    /* =====================================================
       SUMMARY
    ===================================================== */

    .hm-summary-grid {
      display: grid;
      grid-template-columns: 1.5fr 0.75fr 0.75fr;
      gap: 14px;
      margin-bottom: 20px;
    }

    .hm-summary-main,
    .hm-summary-mini {
      border: 1px solid var(--hm-border);
      border-radius: 18px;
      background: #fff;
      box-shadow: 0 7px 24px rgba(30, 38, 64, 0.045);
    }

    .hm-summary-main {
      padding: 20px 22px;
    }

    .hm-summary-main > span,
    .hm-summary-mini > span {
      display: block;
      margin-bottom: 7px;
      color: var(--hm-muted);
      font-size: 14px;
      font-weight: 700;
    }

    .hm-summary-main > strong {
      display: block;
      color: var(--hm-text);
      font-size: 31px;
      font-weight: 900;
      line-height: 1.2;
    }

    .hm-summary-main > strong small {
      margin-right: 4px;
      color: var(--hm-primary);
      font-size: 19px;
    }

    .hm-summary-main p {
      margin: 7px 0 0;
      color: #98a2b3;
      font-size: 13px;
      line-height: 1.5;
    }

    .hm-summary-mini {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 20px;
    }

    .hm-summary-mini strong {
      color: var(--hm-primary);
      font-size: 23px;
      font-weight: 900;
      line-height: 1.25;
      overflow-wrap: anywhere;
    }

    /* =====================================================
       WALLET SECTION
    ===================================================== */

    .hm-wallet-section {
      padding: 22px;
      border: 1px solid var(--hm-border);
      border-radius: 21px;
      background: #fff;
      box-shadow: 0 8px 30px rgba(30, 38, 64, 0.045);
    }

    .hm-section-heading {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 20px;
      padding-bottom: 18px;
      margin-bottom: 18px;
      border-bottom: 1px solid #eef0f3;
    }

    .hm-section-heading span {
      display: block;
      margin-bottom: 3px;
      color: var(--hm-primary);
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }

    .hm-section-heading h2 {
      margin: 0;
      color: var(--hm-text);
      font-size: 23px;
      font-weight: 850;
    }

    .hm-section-heading p {
      margin: 0;
      color: var(--hm-muted);
      font-size: 14px;
    }

    /* =====================================================
       WALLET CARDS
    ===================================================== */

    .hm-wallet-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 14px;
    }

    .hm-wallet-card {
      position: relative;
      overflow: hidden;
      min-height: 190px;
      padding: 18px;
      border: 1px solid #e9ebf0;
      border-radius: 17px;
      background:
        linear-gradient(180deg, #fff 0%, #fbfcfd 100%);
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        border-color 0.2s ease;
    }

    .hm-wallet-card:hover {
      transform: translateY(-3px);
      border-color: #dcd9ff;
      box-shadow: 0 12px 28px rgba(30, 38, 64, 0.08);
    }

    .hm-wallet-card::after {
      content: "";
      position: absolute;
      right: -35px;
      bottom: -50px;
      width: 105px;
      height: 105px;
      border-radius: 50%;
      background: rgba(99, 91, 255, 0.045);
    }

    .hm-wallet-top {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 18px;
    }

    .hm-wallet-icon {
      width: 46px;
      height: 46px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 13px;
      background: #efedff;
      color: var(--hm-primary);
    }

    .hm-wallet-deposit .hm-wallet-icon,
    .hm-wallet-totalDeposit .hm-wallet-icon {
      background: #ecfdf3;
      color: #079455;
    }

    .hm-wallet-principal .hm-wallet-icon {
      background: #eef4ff;
      color: #2970ff;
    }

    .hm-wallet-live .hm-wallet-icon,
    .hm-wallet-income .hm-wallet-icon {
      background: #fff4ed;
      color: #e04f16;
    }

    .hm-wallet-promo .hm-wallet-icon {
      background: #fff8e8;
      color: #d58b00;
    }

    .hm-wallet-withdraw .hm-wallet-icon {
      background: #fff0ee;
      color: #e63e32;
    }

    .hm-wallet-tag {
      padding: 5px 9px;
      border-radius: 20px;
      background: #f4f5f7;
      color: #667085;
      font-size: 11px;
      font-weight: 750;
    }

    .hm-wallet-label {
      position: relative;
      z-index: 2;
      margin-bottom: 5px;
      color: #475467;
      font-size: 15px;
      font-weight: 800;
    }

    .hm-wallet-value {
      position: relative;
      z-index: 2;
      color: var(--hm-text);
      font-size: 26px;
      font-weight: 900;
      line-height: 1.25;
      overflow-wrap: anywhere;
    }

    .hm-wallet-value > span {
      margin-right: 4px;
      color: var(--hm-primary);
      font-size: 17px;
      font-weight: 850;
    }

    .hm-wallet-card p {
      position: relative;
      z-index: 2;
      margin: 8px 0 0;
      color: #98a2b3;
      font-size: 13px;
      line-height: 1.5;
    }

    /* =====================================================
       SECTION-ONLY DATA LOADING
       Keeps the dashboard visible while API data loads.
    ===================================================== */

    .hm-data-skeleton {
      pointer-events: none;
    }

    .hm-skeleton-line,
    .hm-skeleton-box {
      display: block;
      position: relative;
      overflow: hidden;
      background: #eef0f4;
    }

    .hm-skeleton-line {
      border-radius: 999px;
    }

    .hm-skeleton-box {
      border-radius: 13px;
    }

    .hm-skeleton-line::after,
    .hm-skeleton-box::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.78),
        transparent
      );
      animation: hm-skeleton-shimmer 1.25s infinite;
    }

    @keyframes hm-skeleton-shimmer {
      100% {
        transform: translateX(100%);
      }
    }

    .hm-sk-label {
      width: 115px;
      height: 14px;
      margin-bottom: 13px;
    }

    .hm-sk-value {
      width: min(260px, 72%);
      height: 36px;
      margin-bottom: 13px;
    }

    .hm-sk-text {
      width: min(360px, 88%);
      height: 13px;
    }

    .hm-sk-mini-value {
      width: 70%;
      height: 27px;
    }

    .hm-sk-icon {
      width: 46px;
      height: 46px;
    }

    .hm-sk-tag {
      width: 62px;
      height: 22px;
    }

    .hm-sk-wallet-label {
      width: 62%;
      height: 15px;
      margin-bottom: 10px;
    }

    .hm-sk-wallet-value {
      width: 76%;
      height: 29px;
      margin-bottom: 12px;
    }

    .hm-sk-wallet-text {
      width: 88%;
      height: 13px;
    }

    /* =====================================================
       ERROR
    ===================================================== */

    .hm-error-notice {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 13px 16px;
      margin-bottom: 18px;
      border: 1px solid #fecdca;
      border-radius: 13px;
      background: #fff6f5;
    }

    .hm-error-symbol {
      flex: 0 0 32px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      background: #f04438;
      color: #fff;
      font-size: 15px;
      font-weight: 900;
    }

    .hm-error-notice strong {
      display: block;
      margin-bottom: 2px;
      color: #b42318;
      font-size: 14px;
      font-weight: 800;
    }

    .hm-error-notice span {
      display: block;
      color: #d92d20;
      font-size: 13px;
    }

    /* =====================================================
       LOADING
    ===================================================== */

    .hm-loading {
      max-width: 520px;
      margin: 70px auto;
      padding: 40px 25px;
      border: 1px solid var(--hm-border);
      border-radius: 21px;
      background: #fff;
      text-align: center;
      box-shadow: 0 10px 35px rgba(30, 38, 64, 0.06);
    }

    .hm-loader {
      width: 45px;
      height: 45px;
      margin: 0 auto 16px;
      border: 4px solid #e4e2ff;
      border-top-color: var(--hm-primary);
      border-radius: 50%;
      animation: hm-spin 0.75s linear infinite;
    }

    @keyframes hm-spin {
      to {
        transform: rotate(360deg);
      }
    }

    .hm-loading h2 {
      margin: 0 0 7px;
      color: var(--hm-text);
      font-size: 21px;
      font-weight: 850;
    }

    .hm-loading p {
      margin: 0;
      color: var(--hm-muted);
      font-size: 14px;
      line-height: 1.6;
    }

    /* =====================================================
       TABLET
    ===================================================== */

    @media (max-width: 1100px) {
      .hm-wallet-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .hm-summary-grid {
        grid-template-columns: 1fr 1fr;
      }

      .hm-summary-main {
        grid-column: 1 / -1;
      }
    }

    /* =====================================================
       MOBILE
    ===================================================== */

    @media (max-width: 650px) {
      .hm-page {
        padding-bottom: 20px;
      }

      .hm-slider-wrap {
        margin-bottom: 14px;
        border-radius: 0 0 14px 14px;
      }

      .hm-dashboard-header {
        flex-direction: column;
        align-items: flex-start;
        padding: 22px 17px;
        border-radius: 17px;
      }

      .hm-dashboard-header h1 {
        font-size: 24px;
      }

      .hm-dashboard-header p {
        font-size: 14px;
      }

      .hm-eyebrow {
        font-size: 12px;
      }

      .hm-live-status {
        width: 100%;
      }

      .hm-summary-grid {
        grid-template-columns: 1fr 1fr;
        gap: 9px;
      }

      .hm-summary-main {
        grid-column: 1 / -1;
        padding: 17px;
      }

      .hm-summary-main > strong {
        font-size: 26px;
      }

      .hm-summary-mini {
        padding: 15px;
      }

      .hm-summary-mini > span {
        font-size: 13px;
      }

      .hm-summary-mini strong {
        font-size: 18px;
      }

      .hm-wallet-section {
        padding: 15px;
        border-radius: 17px;
      }

      .hm-section-heading {
        align-items: flex-start;
        flex-direction: column;
        gap: 5px;
      }

      .hm-section-heading h2 {
        font-size: 21px;
      }

      .hm-section-heading p {
        font-size: 13px;
      }

      .hm-wallet-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 9px;
      }

      .hm-wallet-card {
        min-height: 175px;
        padding: 14px;
        border-radius: 14px;
      }

      .hm-wallet-icon {
        width: 40px;
        height: 40px;
      }

      .hm-wallet-icon svg {
        width: 22px;
        height: 22px;
      }

      .hm-wallet-tag {
        padding: 4px 7px;
        font-size: 10px;
      }

      .hm-wallet-label {
        font-size: 14px;
      }

      .hm-wallet-value {
        font-size: 20px;
      }

      .hm-wallet-value > span {
        font-size: 14px;
      }

      .hm-wallet-card p {
        font-size: 12px;
      }
    }

    @media (max-width: 390px) {
      .hm-wallet-grid {
        grid-template-columns: 1fr;
      }

      .hm-summary-grid {
        grid-template-columns: 1fr;
      }

      .hm-summary-main {
        grid-column: auto;
      }
    }
  `}</style>
);

export default Home;
